import { useCallback, useEffect, useSyncExternalStore } from "react";
import { addCartItem, getCart, isSignedInToPlatform, updateCartItem } from "../../../network/service/ShopService";

/**
 * The signed-in user's cart, shared by every shop screen (header badge,
 * add buttons). One module-level copy; the server is the source of truth and
 * each change returns the whole cart.
 */
let state = { cart: null, loading: false, error: "" };
const listeners = new Set();
let loadingPromise = null;

function set(patch) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

async function load() {
  if (!isSignedInToPlatform()) return;
  loadingPromise =
    loadingPromise ??
    getCart()
      .then((cart) => set({ cart, error: "" }))
      .catch((error) => set({ error: error.message }))
      .finally(() => {
        loadingPromise = null;
      });
  return loadingPromise;
}

export const SIGN_IN_TO_SHOP = "Sign in to add products to your cart.";

export function useCart() {
  const snapshot = useSyncExternalStore(subscribe, () => state);

  useEffect(() => {
    if (!state.cart) load();
  }, []);

  /** Adds `quantity` of a pack. Rejects with a message safe to show. */
  const add = useCallback(async (priceId, quantity = 1) => {
    if (!isSignedInToPlatform()) throw new Error(SIGN_IN_TO_SHOP);
    set({ loading: true });
    try {
      let cart;
      try {
        cart = await addCartItem(priceId, quantity);
      } catch (error) {
        // Cart holds another store's items: one cart per store, so start over.
        if (!/another store/i.test(error.message)) throw error;
        // eslint-disable-next-line no-alert
        if (!window.confirm("Your cart has items from another store. Start a new cart with this product?")) {
          throw new Error("Cart not changed.");
        }
        cart = await addCartItem(priceId, quantity, true);
      }
      set({ cart, error: "" });
      return cart;
    } finally {
      set({ loading: false });
    }
  }, []);

  /** Sets the quantity of a line already in the cart (0 removes it). */
  const setQuantity = useCallback(async (itemId, quantity) => {
    set({ loading: true });
    try {
      const cart = await updateCartItem(itemId, quantity);
      set({ cart, error: "" });
      return cart;
    } finally {
      set({ loading: false });
    }
  }, []);

  const lineFor = useCallback(
    (priceId) => snapshot.cart?.items.find((item) => item.priceId === priceId && item.available) ?? null,
    [snapshot.cart],
  );

  return {
    cart: snapshot.cart,
    count: snapshot.cart?.itemCount ?? 0,
    busy: snapshot.loading,
    error: snapshot.error,
    add,
    setQuantity,
    lineFor,
    reload: load,
  };
}
